import { useState } from "react";
import {
  ordinal,
  registeredEntries,
  waitlistEntries,
  type EntryContacts,
  type EventEntry,
} from "../lib/types.js";
import { Avatar } from "./Avatar.js";
import { ContactLine } from "./ContactFields.js";
import { Button } from "./ui.js";

/**
 * Who is in, who is waiting, and — where the caller has them — what they said.
 *
 * Shared by the player's view of an event and the organiser's console, because it is one
 * list and rendering it twice is how two lists start disagreeing. The difference between
 * the two screens is not the markup: it is what `notes` contains. `event_notes()` hands a
 * player their own note and an organiser everybody's, so this component shows whatever came
 * back and decides nothing.
 *
 * Contact details work the same way, only more so: `event_contacts` answers organisers and
 * nobody else, so a player's screen never has a map to pass (20261001000300).
 */
export function EntryList({
  entries,
  notes,
  contacts,
  memberId,
  showNoteCount = false,
  onRemove,
  removing,
}: {
  entries: EventEntry[];
  notes: Map<string, string> | undefined;
  /** Organiser view only. How to reach each entrant — a child's guardians, for a child. */
  contacts?: Map<string, EntryContacts>;
  memberId: string | null;
  /** Organiser view only: "· 6 left a note" is only worth saying to somebody who acts on it. */
  showNoteCount?: boolean;
  /** Organiser view only. Absent means no remove control, which is the player's view. */
  onRemove?: (memberId: string) => void;
  removing?: boolean;
}) {
  const registered = registeredEntries(entries);
  const waiting = waitlistEntries(entries);
  // Across both lists. Somebody waiting who can only make the morning is exactly as much of
  // a scheduling problem the moment a place frees up.
  const noted = entries.filter((entry) => notes?.get(entry.memberId)).length;

  const row = (entry: EventEntry, place?: number) => (
    <EntryRow
      key={entry.memberId}
      entry={entry}
      note={notes?.get(entry.memberId) ?? null}
      contact={contacts?.get(entry.memberId)}
      you={entry.memberId === memberId}
      place={place}
      onRemove={onRemove}
      removing={removing}
    />
  );

  return (
    <section className="mb-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <h2 className="micro mb-3">
        Entries
        {showNoteCount && noted > 0 ? ` · ${noted} left a note` : ""}
      </h2>

      {entries.length === 0 ? (
        <p className="text-sm text-ink-muted">Nobody has registered yet.</p>
      ) : null}

      {registered.map((entry) => row(entry))}

      {waiting.length > 0 ? (
        <>
          <p className="micro mt-5">Waiting</p>
          {waiting.map((entry, index) => row(entry, index + 1))}
        </>
      ) : null}
    </section>
  );
}

function EntryRow({
  entry,
  note,
  contact,
  you,
  place,
  onRemove,
  removing,
}: {
  entry: EventEntry;
  note: string | null;
  contact?: EntryContacts;
  you: boolean;
  place?: number;
  onRemove?: (memberId: string) => void;
  removing?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="border-t py-2" style={{ borderColor: "var(--color-line-muted)" }}>
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2 text-sm">
          <Avatar name={entry.displayName} url={entry.avatarUrl} size={26} />
          <span className="truncate">
            {entry.displayName}
            {you ? <span className="micro"> · you</span> : null}
          </span>
        </span>

        <span className="flex shrink-0 items-center gap-2">
          {entry.seed != null && entry.status === "registered" ? (
            <span className="micro" data-testid="entry-seed">
              Seed {entry.seed}
            </span>
          ) : null}
          {place ? <span className="micro">{ordinal(place)}</span> : null}

          {/* Two taps, because this is somebody else's evening and the row it sits on is one
              line high. Not a browser confirm(): those are unstyled, easy to dismiss without
              reading, and on a phone they arrive from nowhere. */}
          {onRemove ? (
            confirming ? (
              <>
                <Button
                  tone="attention"
                  onClick={() => onRemove(entry.memberId)}
                  disabled={removing}
                  style={{ minHeight: 36, padding: "0 10px", fontSize: 13 }}
                >
                  Remove
                </Button>
                <Button
                  tone="quiet"
                  onClick={() => setConfirming(false)}
                  disabled={removing}
                  style={{ minHeight: 36, fontSize: 13 }}
                >
                  Keep
                </Button>
              </>
            ) : (
              <Button
                tone="quiet"
                onClick={() => setConfirming(true)}
                disabled={removing}
                style={{ minHeight: 36, fontSize: 13 }}
              >
                Take out
              </Button>
            )
          ) : null}
        </span>
      </div>

      {contact?.contacts.map((reach, index) => (
        <ContactLine
          key={index}
          flatNo={reach.flatNo}
          phone={reach.phone}
          whose={reach.guardianName ? `${reach.guardianName} (guardian)` : null}
        />
      ))}
      {/* Only where the tournament asked. Elsewhere an entrant without details is simply an
          entrant, and marking them would be asking for something nobody required. */}
      {contact?.missing ? (
        <p className="micro mt-1" style={{ color: "var(--color-attention)" }}>
          {contact.contacts.length === 0 ? "No contact details" : "Contact details incomplete"}
        </p>
      ) : null}

      {note ? <p className="mt-1 text-sm text-ink-muted">&ldquo;{note}&rdquo;</p> : null}
    </div>
  );
}
